// ─── schoolData.js ────────────────────────────────────────────────────────────
// Single source of truth for all peer school data.
// Imported by both Gateway.jsx and AIChartGenerator.jsx.
// Update figures here and both tools stay in sync automatically.

// ─── CONFIDENCE LEVELS ────────────────────────────────────────────────────────
// "V" = Verified  — sourced directly from school website, Wikipedia citing IRS
//                   filings, or a published financial disclosure. Treat as reliable.
// "E" = Estimated — derived from aggregator sites, secondary sources, or
//                   calculated from other verified figures. Check before presenting.
// "L" = Low       — order-of-magnitude only; not found in any primary source.
//                   Do not cite without verification.
// ─────────────────────────────────────────────────────────────────────────────

export const C = { V: "V", E: "E", L: "L" };
export const v = (val, conf) => ({ val, conf });

// ─── GATEWAY TO PREP SCHOOLS DATA ────────────────────────────────────────────
// Sources used:
//  - School websites (taftschool.org, brooksschool.org, loomischaffee.org, etc.)
//  - Wikipedia articles (citing IRS Form 990 filings)
//  - Ivy Talent Education 2024-25 tuition table (cross-referenced)
//  - BoardingSchoolReview, FindingSchool (secondary, used where primary absent)
//  - US Dept of State Office of Overseas Schools fact sheets
// isME: false on all GPS schools; isME: true on Middle East schools below.

export const GPS_SCHOOLS = [
  {
    name: "Bard Academy at Simon's Rock", url: "https://bardacademy.simons-rock.edu",
    state: "MA", location: "Great Barrington, MA", founded: 2007, grades: "9–10",
    gender: "Co-ed", isKings: false, isNew: false, isME: false,
    enrollment:   v(110,   C.E),
    boardingPct:  v(70,    C.E),
    boardingT:    v(72000, C.E),
    dayT:         v(null,  C.E),
    aidPct:       v(40,    C.L),
    aidBudgetM:   v(2.8,   C.L),
    endowmentM:   v(null,  C.L),
    endowmentText: "Part of Bard College",
    notes: "Early-college model for gifted learners; transitions to Bard College at 16",
  },
  {
    name: "Baylor School", url: "https://www.baylorschool.org",
    state: "TN", location: "Chattanooga, TN", founded: 1893, grades: "6–12",
    gender: "Co-ed", isKings: false, isNew: false, isME: false,
    enrollment:   v(1090,  C.E),
    boardingPct:  v(40,    C.E),
    boardingT:    v(57000, C.E),
    dayT:         v(35000, C.E),
    aidPct:       v(25,    C.L),
    aidBudgetM:   v(9,     C.L),
    endowmentM:   v(45,    C.L),
    endowmentText: "~$45M",
    notes: "One of few Gateway peers in the South; grades 6–12",
  },
  {
    // Verified: Wikipedia (IRS filing) endowment $195M; boardingschools.com day $55,400;
    // School website boarding $78,800; 25% on aid avg $55K; enrollment 430; 87% boarding
    name: "Berkshire School", url: "https://www.berkshireschool.org",
    state: "MA", location: "Sheffield, MA", founded: 1907, grades: "9–12, PG",
    gender: "Co-ed", isKings: false, isNew: false, isME: false,
    enrollment:   v(430,   C.V),
    boardingPct:  v(87,    C.V),
    boardingT:    v(78800, C.V),
    dayT:         v(55400, C.V),
    aidPct:       v(25,    C.V),
    aidBudgetM:   v(8.5,   C.E),
    endowmentM:   v(195,   C.V),
    endowmentText: "$195M (IRS filing)",
    notes: "87% boarding; 25% on aid, avg grant $55K; endowment $195M (2023 IRS)",
  },
  {
    // Verified: Wikipedia — enrollment 474, 83% boarding, boarding $78,980, day $55,050,
    // 36% on aid, aid budget $9M, avg boarding grant $54,896
    name: "Blair Academy", url: "https://www.blair.edu",
    state: "NJ", location: "Blairstown, NJ", founded: 1848, grades: "9–PG",
    gender: "Co-ed", isKings: false, isNew: false, isME: false,
    enrollment:   v(474,   C.V),
    boardingPct:  v(83,    C.V),
    boardingT:    v(78980, C.V),
    dayT:         v(55050, C.V),
    aidPct:       v(36,    C.V),
    aidBudgetM:   v(9,     C.V),
    endowmentM:   v(130,   C.E),
    endowmentText: "~$130M",
    notes: "83% boarding; 36% on aid; avg boarding grant $54,896 (Wikipedia/school)",
  },
  {
    // Verified: Brooks school website — boarding $79,450, day $65,250, 35% on aid,
    // aid $6.458M, avg grant $53K, endowment $123.7M (Wikipedia IRS filing)
    name: "Brooks School", url: "https://www.brooksschool.org",
    state: "MA", location: "North Andover, MA", founded: 1926, grades: "9–12",
    gender: "Co-ed", isKings: false, isNew: false, isME: false,
    enrollment:   v(375,   C.E),
    boardingPct:  v(85,    C.E),
    boardingT:    v(79450, C.V),
    dayT:         v(65250, C.V),
    aidPct:       v(35,    C.V),
    aidBudgetM:   v(6.5,   C.V),
    endowmentM:   v(124,   C.V),
    endowmentText: "$123.7M (IRS filing)",
    notes: "35% on aid; aid budget $6.5M; avg grant $53K; endowment $123.7M (school site)",
  },
  {
    name: "Buxton School", url: "https://buxtonschool.org",
    state: "MA", location: "Williamstown, MA", founded: 1928, grades: "9–PG",
    gender: "Co-ed", isKings: false, isNew: false, isME: false,
    enrollment:   v(100,   C.E),
    boardingPct:  v(80,    C.E),
    boardingT:    v(64000, C.E),
    dayT:         v(48000, C.E),
    aidPct:       v(35,    C.L),
    aidBudgetM:   v(1.8,   C.L),
    endowmentM:   v(null,  C.L),
    endowmentText: "Not published",
    notes: "Progressive, arts-integrated curriculum; work program",
  },
  {
    // Verified: cardigan.org — 30% on aid, $3M+ aid budget (2025-26)
    name: "Cardigan Mountain School", url: "https://www.cardigan.org",
    state: "NH", location: "Canaan, NH", founded: 1945, grades: "6–9",
    gender: "Boys", isKings: false, isNew: false, isME: false,
    enrollment:   v(215,   C.E),
    boardingPct:  v(80,    C.E),
    boardingT:    v(73500, C.E),
    dayT:         v(54000, C.E),
    aidPct:       v(30,    C.V),
    aidBudgetM:   v(3,     C.V),
    endowmentM:   v(40,    C.E),
    endowmentText: "~$40M",
    notes: "Junior boarding; 30%+ on aid ($3M budget, school website)",
  },
  {
    // Verified: Ivy Talent table — boarding $79,980 (2024-25)
    name: "Cate School", url: "https://www.cate.org",
    state: "CA", location: "Carpinteria, CA", founded: 1910, grades: "9–12",
    gender: "Co-ed", isKings: false, isNew: false, isME: false,
    enrollment:   v(310,   C.E),
    boardingPct:  v(95,    C.E),
    boardingT:    v(79980, C.V),
    dayT:         v(null,  C.E),
    aidPct:       v(35,    C.E),
    aidBudgetM:   v(8,     C.E),
    endowmentM:   v(130,   C.E),
    endowmentText: "~$130M",
    notes: "~95% boarding; Pacific coast campus; Ivy Talent tuition verified",
  },
  {
    // Verified: choate.edu — 35% on aid, aid budget $16.5M (2025-26), avg 80% of tuition covered
    // Tuition $69,370 (Ivy Talent 2024-25 table)
    name: "Choate Rosemary Hall", url: "https://www.choate.edu",
    state: "CT", location: "Wallingford, CT", founded: 1890, grades: "9–PG",
    gender: "Co-ed", isKings: false, isNew: false, isME: false,
    enrollment:   v(890,   C.E),
    boardingPct:  v(80,    C.E),
    boardingT:    v(69370, C.V),
    dayT:         v(54000, C.E),
    aidPct:       v(35,    C.V),
    aidBudgetM:   v(16.5,  C.V),
    endowmentM:   v(390,   C.E),
    endowmentText: "~$390M",
    notes: "GLADCHEMMS; 35% on aid; aid $16.5M (2025-26, school website)",
  },
  {
    name: "Colorado Rocky Mountain School", url: "https://www.crms.org",
    state: "CO", location: "Carbondale, CO", founded: 1953, grades: "9–12",
    gender: "Co-ed", isKings: false, isNew: false, isME: false,
    enrollment:   v(160,   C.E),
    boardingPct:  v(70,    C.E),
    boardingT:    v(62000, C.E),
    dayT:         v(43000, C.E),
    aidPct:       v(45,    C.E),
    aidBudgetM:   v(3.5,   C.L),
    endowmentM:   v(20,    C.L),
    endowmentText: "~$20M",
    notes: "Progressive, experiential; Roaring Fork Valley",
  },
  {
    // Verified: Ivy Talent table — boarding $81,560 (2024-25, highest in MA group)
    name: "Concord Academy", url: "https://concordacademy.org",
    state: "MA", location: "Concord, MA", founded: 1922, grades: "9–12",
    gender: "Co-ed", isKings: false, isNew: false, isME: false,
    enrollment:   v(380,   C.E),
    boardingPct:  v(50,    C.E),
    boardingT:    v(81560, C.V),
    dayT:         v(65000, C.E),
    aidPct:       v(35,    C.E),
    aidBudgetM:   v(10,    C.E),
    endowmentM:   v(80,    C.E),
    endowmentText: "~$80M",
    notes: "Highest boarding tuition in MA group (Ivy Talent 2024-25)",
  },
  {
    name: "Cushing Academy", url: "https://www.cushing.org",
    state: "MA", location: "Ashburnham, MA", founded: 1865, grades: "9–PG",
    gender: "Co-ed", isKings: false, isNew: false, isME: false,
    enrollment:   v(460,   C.E),
    boardingPct:  v(75,    C.E),
    boardingT:    v(69000, C.E),
    dayT:         v(47000, C.E),
    aidPct:       v(30,    C.L),
    aidBudgetM:   v(7,     C.L),
    endowmentM:   v(40,    C.L),
    endowmentText: "~$40M",
    notes: "Large international student body; postgraduate year",
  },
  {
    // Verified: Ivy Talent table — $74,977 boarding; boardingschools.com — day $57K, 25% aid avg $55K
    name: "Dana Hall School", url: "https://www.danahall.org",
    state: "MA", location: "Wellesley, MA", founded: 1881, grades: "5–12",
    gender: "Girls", isKings: false, isNew: false, isME: false,
    enrollment:   v(470,   C.E),
    boardingPct:  v(40,    C.E),
    boardingT:    v(74977, C.V),
    dayT:         v(57000, C.E),
    aidPct:       v(25,    C.E),
    aidBudgetM:   v(6,     C.E),
    endowmentM:   v(60,    C.E),
    endowmentText: "~$60M",
    notes: "Girls school; near Wellesley College; equestrian program",
  },
  {
    // Verified: Wikipedia (IRS filing) — endowment $920M (Sept 2024); boarding $74,440,
    // day $53,860; 40% on aid; aid budget $15.9M (school + Greenfield Recorder article)
    name: "Deerfield Academy", url: "https://deerfield.edu",
    state: "MA", location: "Deerfield, MA", founded: 1797, grades: "9–12",
    gender: "Co-ed", isKings: false, isNew: false, isME: false,
    enrollment:   v(640,   C.V),
    boardingPct:  v(95,    C.V),
    boardingT:    v(74440, C.V),
    dayT:         v(null,  C.V),
    aidPct:       v(40,    C.V),
    aidBudgetM:   v(15.9,  C.V),
    endowmentM:   v(920,   C.V),
    endowmentText: "$920M (Sept 2024)",
    notes: "GLADCHEMMS; families <$150K pay $0; endowment verified via Wikipedia/IRS",
  },
  {
    // Verified: Ivy Talent table — $73,995 boarding
    name: "Emma Willard School", url: "https://www.emmawillard.org",
    state: "NY", location: "Troy, NY", founded: 1814, grades: "9–PG",
    gender: "Girls", isKings: false, isNew: false, isME: false,
    enrollment:   v(330,   C.E),
    boardingPct:  v(55,    C.E),
    boardingT:    v(73995, C.V),
    dayT:         v(56000, C.E),
    aidPct:       v(35,    C.E),
    aidBudgetM:   v(7,     C.E),
    endowmentM:   v(70,    C.E),
    endowmentText: "~$70M",
    notes: "Historic girls school (est. 1814)",
  },
  {
    // Verified: Ivy Talent table — $72,000; boarding only
    name: "Episcopal High School", url: "https://www.episcopalhighschool.org",
    state: "VA", location: "Alexandria, VA", founded: 1839, grades: "9–12",
    gender: "Co-ed", isKings: false, isNew: false, isME: false,
    enrollment:   v(450,   C.E),
    boardingPct:  v(100,   C.V),
    boardingT:    v(72000, C.V),
    dayT:         v(null,  C.V),
    aidPct:       v(35,    C.E),
    aidBudgetM:   v(10,    C.E),
    endowmentM:   v(130,   C.E),
    endowmentText: "~$130M",
    notes: "100% boarding; Episcopal; near Washington D.C.",
  },
  {
    name: "The Ethel Walker School", url: "https://www.ethelwalker.org",
    state: "CT", location: "Simsbury, CT", founded: 1911, grades: "6–PG",
    gender: "Girls", isKings: false, isNew: false, isME: false,
    enrollment:   v(230,   C.E),
    boardingPct:  v(65,    C.E),
    boardingT:    v(67000, C.E),
    dayT:         v(50000, C.E),
    aidPct:       v(30,    C.L),
    aidBudgetM:   v(4,     C.L),
    endowmentM:   v(30,    C.L),
    endowmentText: "~$30M",
    notes: "Girls school; equestrian program; middle & upper school",
  },
  {
    name: "Fay School", url: "https://www.fayschool.org",
    state: "MA", location: "Southborough, MA", founded: 1866, grades: "K–9",
    gender: "Co-ed", isKings: false, isNew: false, isME: false,
    enrollment:   v(500,   C.E),
    boardingPct:  v(30,    C.E),
    boardingT:    v(72500, C.E),
    dayT:         v(47000, C.E),
    aidPct:       v(20,    C.L),
    aidBudgetM:   v(4.5,   C.L),
    endowmentM:   v(50,    C.E),
    endowmentText: "~$50M",
    notes: "Junior boarding school; key feeder to Gateway peers",
  },
  {
    // Verified: gatewaytoprepschools.com — confirmed Gateway member
    name: "The Frederick Gunn School", url: "https://www.frederickgunnschool.org",
    state: "CT", location: "Washington, CT", founded: 1850, grades: "9–PG",
    gender: "Co-ed", isKings: false, isNew: true, isME: false,
    enrollment:   v(330,   C.E),
    boardingPct:  v(75,    C.E),
    boardingT:    v(71000, C.E),
    dayT:         v(54000, C.E),
    aidPct:       v(30,    C.L),
    aidBudgetM:   v(6,     C.L),
    endowmentM:   v(50,    C.E),
    endowmentText: "~$50M",
    notes: "Formerly The Gunnery; rebranded 2021; outdoor experiential focus",
  },
  {
    name: "Fountain Valley School", url: "https://www.fvs.edu",
    state: "CO", location: "Colorado Springs, CO", founded: 1930, grades: "9–12",
    gender: "Co-ed", isKings: false, isNew: false, isME: false,
    enrollment:   v(230,   C.E),
    boardingPct:  v(75,    C.E),
    boardingT:    v(64000, C.E),
    dayT:         v(45000, C.E),
    aidPct:       v(40,    C.L),
    aidBudgetM:   v(5,     C.L),
    endowmentM:   v(30,    C.L),
    endowmentText: "~$30M",
    notes: "Progressive, outdoor-focused; Rocky Mountain setting",
  },
  {
    // Verified: Ivy Talent table $76,550 boarding; 35% aid estimated
    name: "The Governor's Academy", url: "https://www.thegovernorsacademy.org",
    state: "MA", location: "Byfield, MA", founded: 1763, grades: "9–12",
    gender: "Co-ed", isKings: false, isNew: false, isME: false,
    enrollment:   v(415,   C.E),
    boardingPct:  v(75,    C.E),
    boardingT:    v(76550, C.V),
    dayT:         v(58000, C.E),
    aidPct:       v(35,    C.E),
    aidBudgetM:   v(9,     C.E),
    endowmentM:   v(90,    C.E),
    endowmentText: "~$90M",
    notes: "Oldest continuously operating boarding school in USA (1763)",
  },
  {
    // Verified: Wikipedia (IRS filing) — endowment $550M; boarding $60,895 (Ivy Talent 2024-25),
    // 40-44% on aid, avg grant $46,519 boarding; aid budget $10M (school site)
    name: "Groton School", url: "https://www.groton.org",
    state: "MA", location: "Groton, MA", founded: 1884, grades: "8–12",
    gender: "Co-ed", isKings: false, isNew: false, isME: false,
    enrollment:   v(385,   C.E),
    boardingPct:  v(95,    C.E),
    boardingT:    v(60895, C.V),
    dayT:         v(47000, C.E),
    aidPct:       v(42,    C.V),
    aidBudgetM:   v(10,    C.V),
    endowmentM:   v(550,   C.V),
    endowmentText: "$550M (Wikipedia/IRS)",
    notes: "Founding Gateway peer; needs-blind; families <$150K pay $0; lowest tuition among elite peers",
  },
  {
    name: "Hamden Hall Country Day", url: "https://www.hamdenhall.org",
    state: "CT", location: "Hamden, CT", founded: 1912, grades: "PreK–12",
    gender: "Co-ed", isKings: false, isNew: false, isME: false,
    enrollment:   v(820,   C.E),
    boardingPct:  v(10,    C.E),
    boardingT:    v(62000, C.E),
    dayT:         v(42000, C.E),
    aidPct:       v(18,    C.L),
    aidBudgetM:   v(4,     C.L),
    endowmentM:   v(25,    C.L),
    endowmentText: "~$25M",
    notes: "Primarily day school; small boarding program; PreK–12",
  },
  {
    // Verified: Ivy Talent table $72,390 boarding
    name: "The Hill School", url: "https://www.thehill.org",
    state: "PA", location: "Pottstown, PA", founded: 1851, grades: "9–PG",
    gender: "Co-ed", isKings: false, isNew: false, isME: false,
    enrollment:   v(540,   C.E),
    boardingPct:  v(85,    C.E),
    boardingT:    v(72390, C.V),
    dayT:         v(55000, C.E),
    aidPct:       v(30,    C.E),
    aidBudgetM:   v(10,    C.E),
    endowmentM:   v(175,   C.E),
    endowmentText: "~$175M",
    notes: "~85% boarding; Mid-Atlantic; Episcopal tradition",
  },
  {
    name: "Hopkins School", url: "https://www.hopkins.edu",
    state: "CT", location: "New Haven, CT", founded: 1660, grades: "7–12",
    gender: "Co-ed", isKings: false, isNew: false, isME: false,
    enrollment:   v(720,   C.E),
    boardingPct:  v(5,     C.E),
    boardingT:    v(62000, C.E),
    dayT:         v(46500, C.E),
    aidPct:       v(20,    C.L),
    aidBudgetM:   v(6,     C.L),
    endowmentM:   v(100,   C.E),
    endowmentText: "~$100M",
    notes: "One of America's oldest schools (1660); primarily day; near Yale",
  },
  {
    // Verified: Ivy Talent table $71,170 boarding 2024-25; 36% on aid (AdmissionSight)
    name: "The Hotchkiss School", url: "https://www.hotchkiss.org",
    state: "CT", location: "Lakeville, CT", founded: 1891, grades: "9–PG",
    gender: "Co-ed", isKings: false, isNew: false, isME: false,
    enrollment:   v(590,   C.E),
    boardingPct:  v(95,    C.E),
    boardingT:    v(71170, C.V),
    dayT:         v(null,  C.E),
    aidPct:       v(36,    C.E),
    aidBudgetM:   v(14,    C.E),
    endowmentM:   v(460,   C.E),
    endowmentText: "~$460M",
    notes: "Founding Gateway peer; GLADCHEMMS; Eight Schools Assoc.; needs-blind",
  },
  {
    // Verified: Ivy Talent table $73,450 boarding
    name: "Kent School", url: "https://www.kent-school.edu",
    state: "CT", location: "Kent, CT", founded: 1906, grades: "9–PG",
    gender: "Co-ed", isKings: false, isNew: false, isME: false,
    enrollment:   v(530,   C.E),
    boardingPct:  v(90,    C.E),
    boardingT:    v(73450, C.V),
    dayT:         v(56000, C.E),
    aidPct:       v(30,    C.E),
    aidBudgetM:   v(9,     C.E),
    endowmentM:   v(90,    C.E),
    endowmentText: "~$90M",
    notes: "Episcopalian; ~90% boarding; strong rowing; Housatonic River campus",
  },
  {
    name: "Kents Hill School", url: "https://www.kentshill.org",
    state: "ME", location: "Kents Hill, ME", founded: 1824, grades: "9–PG",
    gender: "Co-ed", isKings: false, isNew: false, isME: false,
    enrollment:   v(260,   C.E),
    boardingPct:  v(65,    C.E),
    boardingT:    v(60000, C.E),
    dayT:         v(40000, C.E),
    aidPct:       v(35,    C.L),
    aidBudgetM:   v(4.5,   C.L),
    endowmentM:   v(20,    C.L),
    endowmentText: "~$20M",
    notes: "One of Maine's oldest schools; strong winter sports tradition",
  },
  {
    name: "Kimball Union Academy", url: "https://www.kua.org",
    state: "NH", location: "Meriden, NH", founded: 1813, grades: "9–PG",
    gender: "Co-ed", isKings: false, isNew: false, isME: false,
    enrollment:   v(330,   C.E),
    boardingPct:  v(80,    C.E),
    boardingT:    v(65000, C.E),
    dayT:         v(45000, C.E),
    aidPct:       v(35,    C.L),
    aidBudgetM:   v(7,     C.L),
    endowmentM:   v(40,    C.L),
    endowmentText: "~$40M",
    notes: "~80% boarding; New Hampshire; strong hockey tradition",
  },
  {
    // FEATURED — King's Academy
    // Verified: edarabia.com citing school — 49% on aid, $7.5M aid budget (2023-24)
    // goodschoolsguide.co.uk — boarding $53,250, day $42,000, enrollment 535
    name: "King's Academy", url: "https://www.kingsacademy.edu.jo",
    state: "JO", location: "Madaba, Jordan", founded: 2007, grades: "7–12",
    gender: "Co-ed", isKings: true, isNew: false, isME: false,
    enrollment:   v(535,   C.V),
    boardingPct:  v(55,    C.E),
    boardingT:    v(53250, C.V),
    dayT:         v(42000, C.V),
    aidPct:       v(49,    C.V),
    aidBudgetM:   v(7.5,   C.V),
    endowmentM:   v(null,  C.L),
    endowmentText: "Not published",
    notes: "Only top-tier boarding school in the Middle East; NEASC accredited; 40+ nationalities",
  },
  {
    name: "Lawrence Academy", url: "https://www.lacademy.edu",
    state: "MA", location: "Groton, MA", founded: 1793, grades: "9–PG",
    gender: "Co-ed", isKings: false, isNew: false, isME: false,
    enrollment:   v(430,   C.E),
    boardingPct:  v(60,    C.E),
    boardingT:    v(68000, C.E),
    dayT:         v(50000, C.E),
    aidPct:       v(30,    C.L),
    aidBudgetM:   v(7,     C.L),
    endowmentM:   v(50,    C.L),
    endowmentText: "~$50M",
    notes: "~60% boarding; progressive ethos; strong arts & outdoor education",
  },
  {
    // Verified: Ivy Talent table $79,500 boarding; Wikipedia avg aid grant ~$60K boarding
    name: "The Lawrenceville School", url: "https://www.lawrenceville.org",
    state: "NJ", location: "Lawrenceville, NJ", founded: 1810, grades: "9–PG",
    gender: "Co-ed", isKings: false, isNew: false, isME: false,
    enrollment:   v(830,   C.E),
    boardingPct:  v(85,    C.E),
    boardingT:    v(79500, C.V),
    dayT:         v(61000, C.E),
    aidPct:       v(30,    C.E),
    aidBudgetM:   v(22,    C.E),
    endowmentM:   v(620,   C.E),
    endowmentText: "~$620M",
    notes: "GLADCHEMMS; house system; Eight Schools Assoc.; needs-blind; near Princeton",
  },
  {
    // Verified: loomischaffee.org — 32% on aid, $13M aid budget (2025-26);
    // Wikipedia — 742 students, 70% boarding; findingschool $74,000 boarding
    name: "Loomis Chaffee School", url: "https://www.loomischaffee.org",
    state: "CT", location: "Windsor, CT", founded: 1874, grades: "9–PG",
    gender: "Co-ed", isKings: false, isNew: false, isME: false,
    enrollment:   v(742,   C.V),
    boardingPct:  v(70,    C.V),
    boardingT:    v(74000, C.V),
    dayT:         v(57000, C.E),
    aidPct:       v(32,    C.V),
    aidBudgetM:   v(13,    C.V),
    endowmentM:   v(250,   C.E),
    endowmentText: "~$250M",
    notes: "32% on aid; $13M aid budget (2025-26 school website); 70% boarding",
  },
  {
    // Verified: Ivy Talent table $71,600 boarding
    name: "The Madeira School", url: "https://www.madeira.org",
    state: "VA", location: "McLean, VA", founded: 1906, grades: "9–12",
    gender: "Girls", isKings: false, isNew: false, isME: false,
    enrollment:   v(330,   C.E),
    boardingPct:  v(50,    C.E),
    boardingT:    v(71600, C.V),
    dayT:         v(55000, C.E),
    aidPct:       v(30,    C.E),
    aidBudgetM:   v(6,     C.E),
    endowmentM:   v(60,    C.E),
    endowmentText: "~$60M",
    notes: "Girls school; co-curriculum internship program; near Washington D.C.",
  },
  {
    // Verified: Ivy Talent table $75,500 boarding
    name: "Mercersburg Academy", url: "https://www.mercersburg.edu",
    state: "PA", location: "Mercersburg, PA", founded: 1893, grades: "9–PG",
    gender: "Co-ed", isKings: false, isNew: false, isME: false,
    enrollment:   v(460,   C.E),
    boardingPct:  v(90,    C.E),
    boardingT:    v(75500, C.V),
    dayT:         v(57000, C.E),
    aidPct:       v(30,    C.E),
    aidBudgetM:   v(9,     C.E),
    endowmentM:   v(130,   C.E),
    endowmentText: "~$130M",
    notes: "~90% boarding; Mid-Atlantic; strong theater & arts tradition",
  },
  {
    // Verified: Ivy Talent table $75,475 boarding
    name: "Middlesex School", url: "https://www.mxschool.edu",
    state: "MA", location: "Concord, MA", founded: 1901, grades: "9–12",
    gender: "Co-ed", isKings: false, isNew: false, isME: false,
    enrollment:   v(390,   C.E),
    boardingPct:  v(90,    C.E),
    boardingT:    v(75475, C.V),
    dayT:         v(57000, C.E),
    aidPct:       v(30,    C.E),
    aidBudgetM:   v(9,     C.E),
    endowmentM:   v(200,   C.E),
    endowmentText: "~$200M",
    notes: "~90% boarding; near Concord/Boston; Select 16",
  },
  {
    // Verified: Ivy Talent table $75,980 boarding
    name: "Milton Academy", url: "https://www.milton.edu",
    state: "MA", location: "Milton, MA", founded: 1798, grades: "K–12",
    gender: "Co-ed", isKings: false, isNew: false, isME: false,
    enrollment:   v(1000,  C.E),
    boardingPct:  v(40,    C.E),
    boardingT:    v(75980, C.V),
    dayT:         v(58000, C.E),
    aidPct:       v(35,    C.E),
    aidBudgetM:   v(18,    C.E),
    endowmentM:   v(400,   C.E),
    endowmentText: "~$400M",
    notes: "K–12 continuum; ~40% boarding in upper school; near Boston; GLADCHEMMS",
  },
  {
    name: "Miss Hall's School", url: "https://www.misshalls.org",
    state: "MA", location: "Pittsfield, MA", founded: 1898, grades: "9–PG",
    gender: "Girls", isKings: false, isNew: false, isME: false,
    enrollment:   v(180,   C.E),
    boardingPct:  v(75,    C.E),
    boardingT:    v(63000, C.E),
    dayT:         v(46000, C.E),
    aidPct:       v(35,    C.L),
    aidBudgetM:   v(3.5,   C.L),
    endowmentM:   v(25,    C.L),
    endowmentText: "~$25M",
    notes: "Small girls school in the Berkshires; leadership-focused curriculum",
  },
  {
    // Verified: Ivy Talent table $74,826 boarding; 43% on aid, $20M budget (school site)
    name: "Northfield Mount Hermon", url: "https://www.nmhschool.org",
    state: "MA", location: "Gill, MA", founded: 1879, grades: "9–PG",
    gender: "Co-ed", isKings: false, isNew: false, isME: false,
    enrollment:   v(650,   C.E),
    boardingPct:  v(90,    C.E),
    boardingT:    v(74826, C.V),
    dayT:         v(56000, C.E),
    aidPct:       v(43,    C.E),
    aidBudgetM:   v(20,    C.E),
    endowmentM:   v(265,   C.E),
    endowmentText: "~$265M",
    notes: "~90% boarding; needs-blind; 43% on aid; progressive mission",
  },
  {
    name: "Northwood School", url: "https://www.northwoodschool.org",
    state: "NY", location: "Lake Placid, NY", founded: 1905, grades: "9–PG",
    gender: "Co-ed", isKings: false, isNew: false, isME: false,
    enrollment:   v(200,   C.E),
    boardingPct:  v(80,    C.E),
    boardingT:    v(65000, C.E),
    dayT:         v(44000, C.E),
    aidPct:       v(30,    C.L),
    aidBudgetM:   v(3.5,   C.L),
    endowmentM:   v(15,    C.L),
    endowmentText: "~$15M",
    notes: "Lake Placid Olympic community; elite winter sports",
  },
  {
    name: "Oak Hill Academy", url: "https://www.oak-hill.net",
    state: "VA", location: "Mouth of Wilson, VA", founded: 1878, grades: "8–PG",
    gender: "Co-ed", isKings: false, isNew: false, isME: false,
    enrollment:   v(150,   C.E),
    boardingPct:  v(95,    C.E),
    boardingT:    v(52000, C.E),
    dayT:         v(null,  C.E),
    aidPct:       v(60,    C.E),
    aidBudgetM:   v(4,     C.L),
    endowmentM:   v(10,    C.L),
    endowmentText: "~$10M",
    notes: "Basketball powerhouse (many NBA alumni); ~60% on aid incl. athletic scholarships",
  },
  {
    // Verified: Ivy Talent table $75,700 boarding
    name: "The Peddie School", url: "https://www.peddie.org",
    state: "NJ", location: "Hightstown, NJ", founded: 1864, grades: "9–PG",
    gender: "Co-ed", isKings: false, isNew: false, isME: false,
    enrollment:   v(550,   C.E),
    boardingPct:  v(75,    C.E),
    boardingT:    v(75700, C.V),
    dayT:         v(57000, C.E),
    aidPct:       v(35,    C.E),
    aidBudgetM:   v(14,    C.E),
    endowmentM:   v(230,   C.E),
    endowmentText: "~$230M",
    notes: "~75% boarding; Mid-Atlantic; needs-blind; strong STEM",
  },
  {
    // Verified: Ivy Talent table $73,780 boarding; Andover website — needs-blind,
    // 47% on aid; endowment ~$1.2B (widely reported)
    name: "Phillips Academy Andover", url: "https://www.andover.edu",
    state: "MA", location: "Andover, MA", founded: 1778, grades: "9–PG",
    gender: "Co-ed", isKings: false, isNew: false, isME: false,
    enrollment:   v(1150,  C.E),
    boardingPct:  v(80,    C.E),
    boardingT:    v(73780, C.V),
    dayT:         v(56000, C.E),
    aidPct:       v(47,    C.V),
    aidBudgetM:   v(34,    C.V),
    endowmentM:   v(1200,  C.E),
    endowmentText: "~$1.2B",
    notes: "GLADCHEMMS; needs-blind; 47% on aid; families <$150K pay $0",
  },
  {
    // Verified: Wikipedia — boarding $69,537, day $54,312 (2024-25); 45% on aid,
    // $29M aid budget; endowment $1.6B (June 2024)
    name: "Phillips Exeter Academy", url: "https://www.exeter.edu",
    state: "NH", location: "Exeter, NH", founded: 1781, grades: "9–PG",
    gender: "Co-ed", isKings: false, isNew: false, isME: false,
    enrollment:   v(1080,  C.E),
    boardingPct:  v(80,    C.E),
    boardingT:    v(69537, C.V),
    dayT:         v(54312, C.V),
    aidPct:       v(45,    C.V),
    aidBudgetM:   v(29,    C.V),
    endowmentM:   v(1600,  C.V),
    endowmentText: "$1.6B (June 2024)",
    notes: "World's largest secondary school endowment; Harkness method; needs-blind; 45% on aid",
  },
  {
    name: "The Putney School", url: "https://www.putneyschool.org",
    state: "VT", location: "Putney, VT", founded: 1935, grades: "9–PG",
    gender: "Co-ed", isKings: false, isNew: false, isME: false,
    enrollment:   v(215,   C.E),
    boardingPct:  v(80,    C.E),
    boardingT:    v(62000, C.E),
    dayT:         v(44000, C.E),
    aidPct:       v(35,    C.L),
    aidBudgetM:   v(4.5,   C.L),
    endowmentM:   v(25,    C.L),
    endowmentText: "~$25M",
    notes: "Progressive, arts-driven; working farm on campus",
  },
  {
    // Verified: gatewaytoprepschools.com & standrews-de.org — 100% boarding;
    // Ivy Talent table $69,400; Wikipedia — 47% on aid, $7.2M aid budget
    name: "St. Andrew's School", url: "https://www.standrews-de.org",
    state: "DE", location: "Middletown, DE", founded: 1929, grades: "9–12",
    gender: "Co-ed", isKings: false, isNew: true, isME: false,
    enrollment:   v(320,   C.V),
    boardingPct:  v(100,   C.V),
    boardingT:    v(69400, C.V),
    dayT:         v(null,  C.V),
    aidPct:       v(47,    C.V),
    aidBudgetM:   v(7.2,   C.V),
    endowmentM:   v(200,   C.E),
    endowmentText: "~$200M",
    notes: "100% boarding; Episcopal; meets 100% of demonstrated need; avg aid grant $49K",
  },
  {
    name: "St. George's School (Vancouver)", url: "https://www.stgeorges.bc.ca",
    state: "BC", location: "Vancouver, BC, Canada", founded: 1930, grades: "K–12",
    gender: "Boys", isKings: false, isNew: false, isME: false,
    enrollment:   v(1650,  C.E),
    boardingPct:  v(15,    C.E),
    boardingT:    v(76050, C.V),
    dayT:         v(35000, C.E),
    aidPct:       v(15,    C.L),
    aidBudgetM:   v(5,     C.L),
    endowmentM:   v(null,  C.L),
    endowmentText: "Not published",
    notes: "Canadian peer; Vancouver day/boarding school for boys; K–12",
  },
  {
    // Verified: Ivy Talent table $76,100 boarding
    name: "St. Mark's School", url: "https://www.stmarksschool.org",
    state: "MA", location: "Southborough, MA", founded: 1865, grades: "9–12",
    gender: "Co-ed", isKings: false, isNew: false, isME: false,
    enrollment:   v(360,   C.E),
    boardingPct:  v(90,    C.E),
    boardingT:    v(76100, C.V),
    dayT:         v(58000, C.E),
    aidPct:       v(30,    C.E),
    aidBudgetM:   v(9,     C.E),
    endowmentM:   v(195,   C.E),
    endowmentText: "~$195M",
    notes: "St. Grottlesex; ~90% boarding; Eight Schools Assoc.; Episcopal",
  },
  {
    // Verified: Ivy Talent table $68,353 boarding; 38% on aid (AdmissionSight);
    // families <$150K pay $0
    name: "St. Paul's School", url: "https://www.sps.edu",
    state: "NH", location: "Concord, NH", founded: 1856, grades: "9–12",
    gender: "Co-ed", isKings: false, isNew: false, isME: false,
    enrollment:   v(540,   C.E),
    boardingPct:  v(100,   C.V),
    boardingT:    v(68353, C.V),
    dayT:         v(null,  C.V),
    aidPct:       v(38,    C.E),
    aidBudgetM:   v(14,    C.E),
    endowmentM:   v(680,   C.E),
    endowmentText: "~$680M",
    notes: "Founding Gateway peer; GLADCHEMMS; 100% boarding; needs-blind; St. Grottlesex",
  },
  {
    name: "St. Stephen's Episcopal School", url: "https://www.sstx.org",
    state: "TX", location: "Austin, TX", founded: 1950, grades: "6–12",
    gender: "Co-ed", isKings: false, isNew: false, isME: false,
    enrollment:   v(700,   C.E),
    boardingPct:  v(30,    C.E),
    boardingT:    v(68500, C.E),
    dayT:         v(43000, C.E),
    aidPct:       v(20,    C.L),
    aidBudgetM:   v(5,     C.L),
    endowmentM:   v(50,    C.L),
    endowmentText: "~$50M",
    notes: "Only Gateway peer in Texas; Episcopal affiliation",
  },
  {
    name: "St. Timothy's School", url: "https://www.stt.org",
    state: "MD", location: "Stevenson, MD", founded: 1882, grades: "9–PG",
    gender: "Girls", isKings: false, isNew: false, isME: false,
    enrollment:   v(170,   C.E),
    boardingPct:  v(80,    C.E),
    boardingT:    v(63000, C.E),
    dayT:         v(47000, C.E),
    aidPct:       v(35,    C.L),
    aidBudgetM:   v(3,     C.L),
    endowmentM:   v(25,    C.L),
    endowmentText: "~$25M",
    notes: "Girls boarding school near Baltimore; Episcopal; equestrian program",
  },
  {
    name: "Salem Academy", url: "https://www.salemacademy.com",
    state: "NC", location: "Winston-Salem, NC", founded: 1772, grades: "9–12",
    gender: "Girls", isKings: false, isNew: false, isME: false,
    enrollment:   v(200,   C.E),
    boardingPct:  v(50,    C.E),
    boardingT:    v(55000, C.E),
    dayT:         v(35000, C.E),
    aidPct:       v(45,    C.L),
    aidBudgetM:   v(4,     C.L),
    endowmentM:   v(30,    C.L),
    endowmentText: "~$30M",
    notes: "One of oldest girls schools in USA (1772); Moravian tradition",
  },
  {
    name: "Salisbury School", url: "https://www.salisburyschool.org",
    state: "CT", location: "Salisbury, CT", founded: 1901, grades: "9–PG",
    gender: "Boys", isKings: false, isNew: false, isME: false,
    enrollment:   v(320,   C.E),
    boardingPct:  v(95,    C.E),
    boardingT:    v(71000, C.E),
    dayT:         v(null,  C.E),
    aidPct:       v(25,    C.L),
    aidBudgetM:   v(5,     C.L),
    endowmentM:   v(60,    C.E),
    endowmentText: "~$60M",
    notes: "Boys school; ~95% boarding; western CT; strong academics and hockey",
  },
  {
    name: "The Storm King School", url: "https://www.sks.org",
    state: "NY", location: "Cornwall-on-Hudson, NY", founded: 1867, grades: "8–PG",
    gender: "Co-ed", isKings: false, isNew: false, isME: false,
    enrollment:   v(170,   C.E),
    boardingPct:  v(80,    C.E),
    boardingT:    v(63000, C.E),
    dayT:         v(46000, C.E),
    aidPct:       v(30,    C.L),
    aidBudgetM:   v(3,     C.L),
    endowmentM:   v(15,    C.L),
    endowmentText: "~$15M",
    notes: "Scenic Hudson Valley campus; strong arts; diverse international body",
  },
  {
    // Verified: Ivy Talent table $74,200 boarding
    name: "Suffield Academy", url: "https://www.suffieldacademy.org",
    state: "CT", location: "Suffield, CT", founded: 1833, grades: "9–PG",
    gender: "Co-ed", isKings: false, isNew: false, isME: false,
    enrollment:   v(420,   C.E),
    boardingPct:  v(80,    C.E),
    boardingT:    v(74200, C.V),
    dayT:         v(56000, C.E),
    aidPct:       v(25,    C.E),
    aidBudgetM:   v(6,     C.E),
    endowmentM:   v(55,    C.E),
    endowmentText: "~$55M",
    notes: "~80% boarding; Northern CT; strong athletics & outdoor education",
  },
  {
    // Verified: Wikipedia — 580 enrolled, 82% boarding, endowment $343M (Feb 2025);
    // school website — 35% on aid, $11M budget; Ivy Talent $75,250 boarding
    name: "The Taft School", url: "https://www.taftschool.org",
    state: "CT", location: "Watertown, CT", founded: 1890, grades: "9–PG",
    gender: "Co-ed", isKings: false, isNew: false, isME: false,
    enrollment:   v(580,   C.V),
    boardingPct:  v(82,    C.V),
    boardingT:    v(75250, C.V),
    dayT:         v(55500, C.E),
    aidPct:       v(35,    C.V),
    aidBudgetM:   v(11,    C.V),
    endowmentM:   v(343,   C.V),
    endowmentText: "$343M (Feb 2025)",
    notes: "GLADCHEMMS; 35% on aid; $11M budget; endowment $343M (school job posting Feb 2025)",
  },
  {
    // Verified: Ivy Talent table $77,880 boarding
    name: "The Thacher School", url: "https://www.thacher.org",
    state: "CA", location: "Ojai, CA", founded: 1889, grades: "9–12",
    gender: "Co-ed", isKings: false, isNew: false, isME: false,
    enrollment:   v(260,   C.E),
    boardingPct:  v(95,    C.E),
    boardingT:    v(77880, C.V),
    dayT:         v(null,  C.E),
    aidPct:       v(35,    C.E),
    aidBudgetM:   v(8,     C.E),
    endowmentM:   v(130,   C.E),
    endowmentText: "~$130M",
    notes: "~95% boarding; every student keeps a horse; Ojai Valley; highly selective",
  },
  {
    name: "Vermont Academy", url: "https://www.vermontacademy.org",
    state: "VT", location: "Saxtons River, VT", founded: 1876, grades: "9–PG",
    gender: "Co-ed", isKings: false, isNew: false, isME: false,
    enrollment:   v(240,   C.E),
    boardingPct:  v(80,    C.E),
    boardingT:    v(62000, C.E),
    dayT:         v(43000, C.E),
    aidPct:       v(35,    C.L),
    aidBudgetM:   v(4.5,   C.L),
    endowmentM:   v(15,    C.L),
    endowmentText: "~$15M",
    notes: "~80% boarding; Vermont; strong outdoor program & ski racing",
  },
  {
    name: "Wayland Academy", url: "https://www.wayland.org",
    state: "WI", location: "Beaver Dam, WI", founded: 1855, grades: "9–PG",
    gender: "Co-ed", isKings: false, isNew: false, isME: false,
    enrollment:   v(200,   C.E),
    boardingPct:  v(65,    C.E),
    boardingT:    v(53000, C.E),
    dayT:         v(35000, C.E),
    aidPct:       v(35,    C.L),
    aidBudgetM:   v(3,     C.L),
    endowmentM:   v(15,    C.L),
    endowmentText: "~$15M",
    notes: "Only Gateway peer in Wisconsin; diverse student body",
  },
  {
    // Verified: Ivy Talent table $84,070 boarding
    name: "The Webb Schools", url: "https://www.webb.org",
    state: "CA", location: "Claremont, CA", founded: 1922, grades: "9–12",
    gender: "Co-ed", isKings: false, isNew: false, isME: false,
    enrollment:   v(400,   C.E),
    boardingPct:  v(80,    C.E),
    boardingT:    v(84070, C.V),
    dayT:         v(63000, C.E),
    aidPct:       v(30,    C.E),
    aidBudgetM:   v(9,     C.E),
    endowmentM:   v(90,    C.E),
    endowmentText: "~$90M",
    notes: "~80% boarding; Claremont, CA; paleontology program; highest boarding tuition in group",
  },
  {
    // Verified: Ivy Talent table $73,370 boarding
    name: "Westminster School (CT)", url: "https://www.westminster-school.org",
    state: "CT", location: "Simsbury, CT", founded: 1888, grades: "9–PG",
    gender: "Co-ed", isKings: false, isNew: false, isME: false,
    enrollment:   v(400,   C.E),
    boardingPct:  v(90,    C.E),
    boardingT:    v(73370, C.V),
    dayT:         v(55000, C.E),
    aidPct:       v(28,    C.E),
    aidBudgetM:   v(7,     C.E),
    endowmentM:   v(75,    C.E),
    endowmentText: "~$75M",
    notes: "~90% boarding; Simsbury, CT; strong athletics",
  },
  {
    name: "Wilbraham & Monson Academy", url: "https://www.wma.us",
    state: "MA", location: "Wilbraham, MA", founded: 1817, grades: "7–PG",
    gender: "Co-ed", isKings: false, isNew: false, isME: false,
    enrollment:   v(540,   C.E),
    boardingPct:  v(50,    C.E),
    boardingT:    v(61000, C.E),
    dayT:         v(38000, C.E),
    aidPct:       v(30,    C.L),
    aidBudgetM:   v(7,     C.L),
    endowmentM:   v(30,    C.L),
    endowmentText: "~$30M",
    notes: "~50% boarding; large international student body; Methodist heritage",
  },
  {
    name: "The Winchendon School", url: "https://www.winchendon.org",
    state: "MA", location: "Winchendon, MA", founded: 1926, grades: "9–PG",
    gender: "Co-ed", isKings: false, isNew: false, isME: false,
    enrollment:   v(225,   C.E),
    boardingPct:  v(85,    C.E),
    boardingT:    v(64000, C.E),
    dayT:         v(47000, C.E),
    aidPct:       v(35,    C.L),
    aidBudgetM:   v(4.5,   C.L),
    endowmentM:   v(10,    C.L),
    endowmentText: "~$10M",
    notes: "Specializes in students who learn differently; postgraduate year",
  },
];


// ─── MIDDLE EAST SCHOOLS DATA ─────────────────────────────────────────────────
// ALL ME schools are DAY schools. Use King's day tuition ($42,000) for comparison.
// Financial aid is almost universally unpublished — employer/govt-sponsored model.
// UAE/Qatar fees are government-regulated (KHDA/ADEK) and highly reliable.
// Currency conversions: AED ÷3.67, QAR ÷3.64, SAR ÷3.75, KWD ×3.25, EGP ÷50

export const ME_SCHOOLS = [

  // ── UAE — DUBAI ──────────────────────────────────────────────────────────────

  {
    // Verified: WhichSchoolAdvisor — AED 59,180–87,268 (2024-25); ~2,000 students,
    // 80+ nationalities; school website confirms not-for-profit; founded 1966.
    // High school day tuition AED 87,268 = ~$23,779 USD. No financial aid published.
    name: "American School of Dubai",
    url: "https://www.asdubai.org",
    state: "Dubai", location: "Al Barsha, Dubai, UAE", founded: 1966, grades: "PreK–12",
    gender: "Co-ed", isKings: false, isNew: false, isME: true,
    enrollment:   v(2000,  C.V),
    boardingPct:  v(0,     C.V),
    boardingT:    v(null,  C.V),
    dayT:         v(24333, C.V),   // AED 89,319 G1-12 flat ÷ 3.67 (school website doris.school verified 2025-26)
    aidPct:       v(null,  C.L),
    aidBudgetM:   v(null,  C.L),
    endowmentM:   v(null,  C.L),
    endowmentText: "Not published",
    notes: "Oldest US school in UAE (1966); not-for-profit; 80+ nationalities; KHDA Outstanding",
  },
  {
    // Verified: WhichSchoolAdvisor — G1-12 flat AED 91,158 (~$24,839 USD), 3,088 students
    // (2024 roll); KHDA Outstanding (only US curriculum school in Dubai to achieve this).
    // For-profit GEMS Education. No need-based aid published.
    name: "GEMS Dubai American Academy",
    url: "https://www.gemsaa-dubai.com",
    state: "Dubai", location: "Al Safa, Dubai, UAE", founded: 2000, grades: "KG1–12",
    gender: "Co-ed", isKings: false, isNew: false, isME: true,
    enrollment:   v(3088,  C.V),
    boardingPct:  v(0,     C.V),
    boardingT:    v(null,  C.V),
    dayT:         v(25421, C.V),   // AED 93,300 G9-12 ÷ 3.67 (WhichSchoolAdvisor 2025-26, KHDA-approved)
    aidPct:       v(null,  C.L),
    aidBudgetM:   v(null,  C.L),
    endowmentM:   v(null,  C.L),
    endowmentText: "For-profit (GEMS Education)",
    notes: "Largest US curriculum school in Dubai; KHDA Outstanding; IB Diploma offered; NEASC accredited",
  },
  {
    // Verified: School website (KHDA-approved) — Grade 12 AED 123,442 (~$33,635 USD) 2025-26.
    // IB-only curriculum (PYP/MYP/DP). GEMS Education for-profit. ~1,800 students.
    // One of the most expensive schools in the UAE. No financial aid program.
    name: "GEMS World Academy – Dubai",
    url: "https://www.gemsworldacademy-dubai.com",
    state: "Dubai", location: "Al Barsha, Dubai, UAE", founded: 2007, grades: "PreK–12",
    gender: "Co-ed", isKings: false, isNew: false, isME: true,
    enrollment:   v(1800,  C.E),
    boardingPct:  v(0,     C.V),
    boardingT:    v(null,  C.V),
    dayT:         v(33635, C.V),   // AED 123,442 Grade 12 ÷ 3.67 (school website 2025-26)
    aidPct:       v(null,  C.L),
    aidBudgetM:   v(null,  C.L),
    endowmentM:   v(null,  C.L),
    endowmentText: "For-profit (GEMS Education)",
    notes: "Pure IB continuum (PYP/MYP/DP); highest fees in Dubai group; KHDA Outstanding; 100+ nationalities",
  },
  {
    // Estimated: JESS — top premium Dubai school; KHDA Outstanding. ~2,400 students.
    // High school fees approximately AED 80,000–90,000 (WhichSchoolAdvisor range estimate).
    name: "Jumeirah English Speaking School (JESS)",
    url: "https://www.jess.sch.ae",
    state: "Dubai", location: "Arabian Ranches, Dubai, UAE", founded: 1975, grades: "FS1–13",
    gender: "Co-ed", isKings: false, isNew: false, isME: true,
    enrollment:   v(2400,  C.E),
    boardingPct:  v(0,     C.V),
    boardingT:    v(null,  C.V),
    dayT:         v(22000, C.E),   // AED ~80,800 HS ÷ 3.67 (WhichSchoolAdvisor range estimate)
    aidPct:       v(null,  C.L),
    aidBudgetM:   v(null,  C.L),
    endowmentM:   v(null,  C.L),
    endowmentText: "Not published",
    notes: "British/American curriculum; KHDA Outstanding; strong university placement; 80+ nationalities",
  },

  // ── UAE — ABU DHABI ──────────────────────────────────────────────────────────

  {
    // Verified: School website + Bayut — AED 56,526–97,850 (2025-26);
    // high school top AED 97,850 = ~$26,662 USD. Non-profit, US Dept of State affiliated.
    // ADEK Outstanding. New campus on Saadiyat Island opened January 2024. ~1,300 students.
    name: "American Community School of Abu Dhabi",
    url: "https://www.acs.sch.ae",
    state: "Abu Dhabi", location: "Saadiyat Island, Abu Dhabi, UAE", founded: 1972, grades: "KG1–12",
    gender: "Co-ed", isKings: false, isNew: false, isME: true,
    enrollment:   v(1300,  C.E),
    boardingPct:  v(0,     C.V),
    boardingT:    v(null,  C.V),
    dayT:         v(26987, C.V),   // AED 99,060 G9-12 ÷ 3.67 (official ACS fee schedule PDF 2025-26)
    aidPct:       v(null,  C.L),
    aidBudgetM:   v(null,  C.L),
    endowmentM:   v(null,  C.L),
    endowmentText: "Not published (non-profit)",
    notes: "Non-profit; US Dept of State affiliated; AP + IB Diploma; ADEK Outstanding; Saadiyat Island campus",
  },
  {
    // Verified: WhichSchoolAdvisor — G1-12 AED 73,670 (~$20,074 USD);
    // GEMS Education for-profit. ADEK Very Good. ~900 students. NEASC accredited.
    name: "GEMS American Academy – Abu Dhabi",
    url: "https://www.gemsaa-abudhabi.com",
    state: "Abu Dhabi", location: "Khalifa City, Abu Dhabi, UAE", founded: 2008, grades: "KG1–12",
    gender: "Co-ed", isKings: false, isNew: false, isME: true,
    enrollment:   v(900,   C.E),
    boardingPct:  v(0,     C.V),
    boardingT:    v(null,  C.V),
    dayT:         v(20074, C.V),   // AED 73,670 G1-12 ÷ 3.67 (WhichSchoolAdvisor 2024-25)
    aidPct:       v(null,  C.L),
    aidBudgetM:   v(null,  C.L),
    endowmentM:   v(null,  C.L),
    endowmentText: "For-profit (GEMS Education)",
    notes: "For-profit GEMS; US curriculum + IB Diploma; ADEK Very Good; NEASC accredited",
  },

  // ── QATAR ────────────────────────────────────────────────────────────────────

  {
    // Verified: US State Dept Fact Sheet (Dec 2024) — G6-12 tuition $20,498 USD (2023-24);
    // ~1,800 students; non-profit; founded 1988; NEASC + CIS accredited.
    // Capital fee $3,400/yr additional. Financial aid not publicly published.
    name: "American School of Doha",
    url: "https://asd.sch.qa",
    state: "Qatar", location: "Doha, Qatar", founded: 1988, grades: "PreK–12",
    gender: "Co-ed", isKings: false, isNew: false, isME: true,
    enrollment:   v(1800,  C.E),
    boardingPct:  v(0,     C.V),
    boardingT:    v(null,  C.V),
    dayT:         v(20498, C.V),   // G6-12 USD (US State Dept Fact Sheet 2023-24)
    aidPct:       v(null,  C.L),
    aidBudgetM:   v(null,  C.L),
    endowmentM:   v(null,  C.L),
    endowmentText: "Not published (non-profit)",
    notes: "Non-profit; 26-acre campus; NEASC + CIS accredited; US Dept of State affiliated; 70+ nationalities",
  },
  {
    // Estimated: Doha College — leading British curriculum school in Doha;
    // high school ~QAR 55,000–60,000 (~$15,000–16,500 USD). ~2,200 students.
    name: "Doha College",
    url: "https://www.dohacollege.com",
    state: "Qatar", location: "Al Wajbah, Doha, Qatar", founded: 1980, grades: "KG–13",
    gender: "Co-ed", isKings: false, isNew: false, isME: true,
    enrollment:   v(2200,  C.E),
    boardingPct:  v(0,     C.V),
    boardingT:    v(null,  C.V),
    dayT:         v(16000, C.E),   // QAR ~58,000 ÷ 3.64 (secondary sources)
    aidPct:       v(null,  C.L),
    aidBudgetM:   v(null,  C.L),
    endowmentM:   v(null,  C.L),
    endowmentText: "Not published",
    notes: "British curriculum (GCSE/A-Level); non-profit; highly regarded in Doha; 70+ nationalities",
  },

  // ── SAUDI ARABIA ─────────────────────────────────────────────────────────────

  {
    // Verified: US State Dept Fact Sheet (2023-24) — HS tuition $29,470 USD (inc. 15% VAT);
    // enrollment 1,499 (2023-24); non-profit; founded 1963; NEASC + CIS accredited; IB Diploma.
    // 60+ nationalities; 379 American, 262 Saudi, 858 third-country nationals.
    name: "American International School – Riyadh",
    url: "https://www.aisr.org",
    state: "Saudi Arabia", location: "Riyadh, Saudi Arabia", founded: 1963, grades: "PK–12",
    gender: "Co-ed", isKings: false, isNew: false, isME: true,
    enrollment:   v(1499,  C.V),
    boardingPct:  v(0,     C.V),
    boardingT:    v(null,  C.V),
    dayT:         v(28893, C.V),   // SAR 108,346 G9-12 inc. 15% VAT ÷ 3.75 (WhichSchoolAdvisor 2025-26 verified)
    aidPct:       v(null,  C.L),
    aidBudgetM:   v(null,  C.L),
    endowmentM:   v(null,  C.L),
    endowmentText: "Not published (non-profit)",
    notes: "Non-profit; NEASC + CIS accredited; IB Diploma; US Dept of State affiliated; 60+ nationalities",
  },
  {
    // Estimated: ASD (American School Dhahran) — part of ISG group (formerly SAIS);
    // SAR range 29,975–86,600 across all ISG schools/grades; HS ~SAR 84,000 (~$22,400).
    // Founded 1962; ~1,200 students; NEASC + MSA + IBO accredited.
    name: "American School Dhahran (ISG)",
    url: "https://www.isg-education.com",
    state: "Saudi Arabia", location: "Dhahran, Saudi Arabia", founded: 1962, grades: "PK–12",
    gender: "Co-ed", isKings: false, isNew: false, isME: true,
    enrollment:   v(1200,  C.E),
    boardingPct:  v(0,     C.V),
    boardingT:    v(null,  C.V),
    dayT:         v(22400, C.E),   // SAR ~84,000 ÷ 3.75 estimated HS rate (school website range)
    aidPct:       v(null,  C.L),
    aidBudgetM:   v(null,  C.L),
    endowmentM:   v(null,  C.L),
    endowmentText: "Not published",
    notes: "Formerly SAIS; part of ISG group; AP + IB Diploma; MSA + NEASC accredited; 70+ nationalities",
  },
  {
    // Estimated: AISJ — non-profit, US Dept of State affiliated; ~800 students; NEASC accredited.
    // Fee schedule not publicly published. ~$18,000–22,000 USD at HS (secondary sources).
    name: "American International School of Jeddah",
    url: "https://aisj.edu.sa",
    state: "Saudi Arabia", location: "Jeddah, Saudi Arabia", founded: 1963, grades: "PK–12",
    gender: "Co-ed", isKings: false, isNew: false, isME: true,
    enrollment:   v(800,   C.E),
    boardingPct:  v(0,     C.V),
    boardingT:    v(null,  C.V),
    dayT:         v(28029, C.V),   // SAR 105,110 G11-12 incl. 15% VAT ÷ 3.75 (school website aisj.edu.sa screenshot verified)
    aidPct:       v(null,  C.L),
    aidBudgetM:   v(null,  C.L),
    endowmentM:   v(null,  C.L),
    endowmentText: "Not published (non-profit)",
    notes: "Non-profit; US Dept of State affiliated; NEASC accredited; AP curriculum",
  },

  // ── KUWAIT ───────────────────────────────────────────────────────────────────

  {
    // Verified base: US State Dept Fact Sheet (2020-21) — G9-12 KD 3,893 (~$12,652).
    // Estimated 2024-25 approximately KD 4,500 given misalignment (~$14,625).
    // ~2,300 students; privately owned; founded 1964; US curriculum.
    name: "The American School of Kuwait",
    url: "https://ask.edu.kw",
    state: "Kuwait", location: "Hawally, Kuwait City, Kuwait", founded: 1964, grades: "PreK–12",
    gender: "Co-ed", isKings: false, isNew: false, isME: true,
    enrollment:   v(2300,  C.E),
    boardingPct:  v(0,     C.V),
    boardingT:    v(null,  C.V),
    dayT:         v(15275, C.E),   // KD 4,700 × 3.25 (international-schools-database 2025-26)
    aidPct:       v(null,  C.L),
    aidBudgetM:   v(null,  C.L),
    endowmentM:   v(null,  C.L),
    endowmentText: "Not published",
    notes: "Founded 1964; US curriculum; privately owned; College Board test center; 50+ nationalities",
  },
  {
    // Estimated: AIS Kuwait — founded 1991; Salmiya.
    // Fees approximately KD 3,200–3,800 for HS (secondary sources). ~1,200 students.
    name: "American International School of Kuwait",
    url: "https://ais.edu.kw",
    state: "Kuwait", location: "Salmiya, Kuwait City, Kuwait", founded: 1991, grades: "KG–12",
    gender: "Co-ed", isKings: false, isNew: false, isME: true,
    enrollment:   v(1200,  C.E),
    boardingPct:  v(0,     C.V),
    boardingT:    v(null,  C.V),
    dayT:         v(11700, C.E),   // KD ~3,600 × 3.25 estimated (secondary sources)
    aidPct:       v(null,  C.L),
    aidBudgetM:   v(null,  C.L),
    endowmentM:   v(null,  C.L),
    endowmentText: "Not published",
    notes: "US curriculum; NEASC accredited; founded 1991; AP + SAT prep focus",
  },

  // ── EGYPT ────────────────────────────────────────────────────────────────────

  {
    // Partially verified: AIS Egypt — tuition in EGP; ~EGP 350,000–450,000/yr HS (2024-25).
    // At ~50 EGP/USD ≈ $7,000–9,000. NOTE: EGP has depreciated sharply; USD equivalent
    // is volatile. Non-Egyptian families may pay a separate hard-currency fee.
    // ~1,500 students; founded 1990; NEASC accredited; IB Diploma option.
    name: "American International School in Egypt (Main Campus)",
    url: "https://www.aisegypt.com",
    state: "Egypt", location: "Kattameya, Cairo, Egypt", founded: 1990, grades: "KG1–12",
    gender: "Co-ed", isKings: false, isNew: false, isME: true,
    enrollment:   v(1500,  C.E),
    boardingPct:  v(0,     C.V),
    boardingT:    v(null,  C.V),
    dayT:         v(8000,  C.L),   // EGP ~400,000 ÷ 50 approx (highly volatile; treat with caution)
    aidPct:       v(null,  C.L),
    aidBudgetM:   v(null,  C.L),
    endowmentM:   v(null,  C.L),
    endowmentText: "Not published",
    notes: "NEASC accredited; IB Diploma; EGP fees — USD conversion unreliable due to currency volatility",
  },
  {
    // Estimated: Cairo American College (CAC) — one of Egypt's oldest and most prestigious
    // international schools; US curriculum; NEASC accredited. Maadi campus. Founded 1945.
    // ~1,000 students. Fees not publicly published in USD; ~$15,000–20,000 estimated from
    // aggregator data and parent reports.
    name: "Cairo American College",
    url: "https://www.cacegypt.org",
    state: "Egypt", location: "Maadi, Cairo, Egypt", founded: 1945, grades: "KG–12",
    gender: "Co-ed", isKings: false, isNew: false, isME: true,
    enrollment:   v(1000,  C.E),
    boardingPct:  v(0,     C.V),
    boardingT:    v(null,  C.V),
    dayT:         v(17000, C.L),   // ~$17K estimated HS (aggregators + parent reports; not published)
    aidPct:       v(null,  C.L),
    aidBudgetM:   v(null,  C.L),
    endowmentM:   v(null,  C.L),
    endowmentText: "Not published",
    notes: "One of oldest US schools in ME (1945); Maadi, Cairo; NEASC accredited; historically prestigious",
  },

  // ── BAHRAIN ──────────────────────────────────────────────────────────────────

  {
    // Partially verified: ASB — non-profit; US Dept of State affiliated; ~700 students.
    // Founded 1968; NEASC accredited. ~$15,000–18,000 USD for HS (secondary sources;
    // fee schedule not publicly accessible).
    name: "American School of Bahrain",
    url: "https://www.asbah.org",
    state: "Bahrain", location: "Manama, Bahrain", founded: 1968, grades: "PK–12",
    gender: "Co-ed", isKings: false, isNew: false, isME: true,
    enrollment:   v(700,   C.E),
    boardingPct:  v(0,     C.V),
    boardingT:    v(null,  C.V),
    dayT:         v(16000, C.E),   // ~$16K estimated HS (secondary sources)
    aidPct:       v(null,  C.L),
    aidBudgetM:   v(null,  C.L),
    endowmentM:   v(null,  C.L),
    endowmentText: "Not published (non-profit)",
    notes: "Non-profit; US Dept of State affiliated; NEASC accredited; strong expat community",
  },

  // ── JORDAN ───────────────────────────────────────────────────────────────────

  {
    // Verified: ACS Amman school website — founded 1955; non-profit; NEASC accredited;
    // US Dept of State affiliated. ~1,200 students. HS tuition approximately $14,500
    // (published fee schedule approximate; school site). King's closest Jordanian day peer.
    name: "American Community School of Amman",
    url: "https://www.acs.edu.jo",
    state: "Jordan", location: "Amman, Jordan", founded: 1955, grades: "PK–12",
    gender: "Co-ed", isKings: false, isNew: false, isME: true,
    enrollment:   v(1200,  C.E),
    boardingPct:  v(0,     C.V),
    boardingT:    v(null,  C.V),
    dayT:         v(27450, C.V),   // G9-12 USD $27,450 (school fee schedule 2025-26, screenshot verified)
    aidPct:       v(20,    C.L),
    aidBudgetM:   v(null,  C.L),
    endowmentM:   v(null,  C.L),
    endowmentText: "Not published",
    notes: "King's Academy's closest Jordanian day-school peer; non-profit; NEASC accredited; founded 1955",
  },
  {
    // Partially verified: ICS Amman — American/IB curriculum; Amman; non-profit.
    // ~900 students; founded 1994. Tuition approximately $10,000–14,000 USD for HS (limited data).
    name: "International Community School of Amman",
    url: "https://www.ics-amman.edu.jo",
    state: "Jordan", location: "Amman, Jordan", founded: 1994, grades: "PK–12",
    gender: "Co-ed", isKings: false, isNew: false, isME: true,
    enrollment:   v(900,   C.E),
    boardingPct:  v(0,     C.V),
    boardingT:    v(null,  C.V),
    dayT:         v(12000, C.L),   // ~$12K HS estimated (limited public data)
    aidPct:       v(null,  C.L),
    aidBudgetM:   v(null,  C.L),
    endowmentM:   v(null,  C.L),
    endowmentText: "Not published",
    notes: "American/IB curriculum; Amman; non-profit; serves expat and local Jordanian community",
  },
];


// ─── COMPUTE DERIVED FIELDS ───────────────────────────────────────────────────
// Called once per dataset mode change; computes gross revenue and tuition:aid ratio.
export function computeDerived(arr) {
  return arr.map(s => {
    const boardCount = Math.round((s.enrollment.val || 0) * (s.boardingPct.val || 0) / 100);
    const dayCount   = (s.enrollment.val || 0) - boardCount;
    const dayRate    = s.dayT.val || ((s.boardingT.val || 0) * 0.75);
    const grossM     = (boardCount * (s.boardingT.val || 0) + dayCount * dayRate) / 1e6;
    const ratio      = s.aidBudgetM.val ? Math.round(grossM / s.aidBudgetM.val * 10) / 10 : null;
    const ratioConf  = [s.boardingT.conf, s.aidBudgetM.conf].includes(C.L) ? C.L
      : [s.boardingT.conf, s.aidBudgetM.conf].includes(C.E) ? C.E : C.V;
    return { ...s, grossM, ratio, ratioConf };
  });
}

// ─── ESA SCHOOLS ─────────────────────────────────────────────────────────────
export const ESA_NAMES = new Set([
  "Phillips Academy Andover",
  "Phillips Exeter Academy",
  "Choate Rosemary Hall",
  "Deerfield Academy",
  "The Hotchkiss School",
  "The Lawrenceville School",
  "Northfield Mount Hermon",
  "St. Paul's School",
]);